import React, { useEffect, useState, useMemo } from "react";
import {
  Container,
  Typography,
  TextField,
  Autocomplete,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { getLogs } from "../api/logs";
import { useNavigate } from "react-router-dom";
import { getDevices } from "../api/devices";
import { downloadCSV } from "../utils/csvUtils";
import TopNavBar from "../components/TopNavBar";

// Extend dayjs with timezone support
dayjs.extend(utc);
dayjs.extend(timezone);

export default function LogsPage() {
  const navigate = useNavigate();
  const username = sessionStorage.getItem("username");

  const [selectedDevices, setSelectedDevices] = useState([]);
  const [devices, setDevices] = useState([]);
  const [start, setStart] = useState(null);
  const [end, setEnd] = useState(dayjs());
  const [logs, setLogs] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedManufacturer, setSelectedManufacturer] = useState("all");
  const [showAllColumns, setShowAllColumns] = useState(false);

  // For DataGrid
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 100,
  });
  const [totalCount, setTotalCount] = useState(0);

  // For CSV download
  const [downloadProgress, setDownloadProgress] = useState(null);

  // Load devices for dropdown
  useEffect(() => {
    const fetchDevices = async () => {
      try {
        const data = await getDevices();
        setDevices(data);
      } catch (err) {
        console.error("Error fetching devices:", err);
      }
    };
    fetchDevices();
  }, []);

  useEffect(() => {
    if (selectedDevices.length && start && end) {
      handleSearch();
    }
    // eslint-disable-next-line
  }, [paginationModel]);

  const handleSearch = async () => {
    console.log("Slected devices:", selectedDevices);
    if (!selectedDevices.length || !start || !end) {
      setError("Please select device(s), start and end time.");
      return;
    }

    if (!dayjs.isDayjs(start) || !start.isValid()) {
      setError("Invalid start date");
      return;
    }
    if (!dayjs.isDayjs(end) || !end.isValid()) {
      setError("Invalid end date");
      return;
    }

    setLoading(true);
    try {
      setError(null);
      // Convert local time to UTC for backend
      const formattedStart = dayjs(start).utc().format("YYYY-MM-DD HH:mm:ss");
      const formattedEnd = dayjs(end).utc().format("YYYY-MM-DD HH:mm:ss");

      const data = await getLogs({
        device_id: selectedDevices,
        start: formattedStart,
        end: formattedEnd,
        limit: paginationModel.pageSize,
        offset: paginationModel.page * paginationModel.pageSize,
      });

      // Convert backend UTC timestamps to local timezone immediately
      const localRows = data.rows.map((row) => ({
        ...row,
        timestamp: row.timestamp
          ? dayjs.utc(row.timestamp).local().format("YYYY-MM-DD HH:mm:ss:SSS")
          : "",
      }));
      console.log("Fetched logs:", localRows);

      setLogs(localRows);
      setTotalCount(data.total);
    } catch (err) {
      console.error("Error fetching logs:", err);
      setError("Failed to fetch logs. Check inputs and try again.");
    } finally {
      setLoading(false);
    }
  };

  // Download all logs for all selected devices
  const handleDownloadAll = async () => {
    if (!selectedDevices.length || !start || !end) {
      setError("Please select device(s), start and end time.");
      return;
    }

    setLoading(true);
    setDownloadProgress("Starting download...");

    try {
      const formattedStart = dayjs(start).utc().format("YYYY-MM-DD HH:mm:ss");
      const formattedEnd = dayjs(end).utc().format("YYYY-MM-DD HH:mm:ss");

      let offset = 0;
      const limit = 1000;
      let allLogs = [];
      let total = 0;

      const firstPage = await getLogs({
        device_id: selectedDevices,
        start: formattedStart,
        end: formattedEnd,
        limit,
        offset,
      });

      if (!firstPage) throw new Error("No data returned");

      allLogs = [...firstPage.rows];
      total = firstPage.total || 0;

      setDownloadProgress(`Fetched ${allLogs.length}/${total} logs...`);

      while (allLogs.length < total) {
        offset += limit;
        const nextPage = await getLogs({
          device_id: selectedDevices,
          start: formattedStart,
          end: formattedEnd,
          limit,
          offset,
        });
        if (!nextPage || !nextPage.rows.length) break;
        allLogs.push(...nextPage.rows);

        setDownloadProgress(`Fetched ${allLogs.length}/${total} logs...`);
      }

      // ✅ Convert timestamps to local timezone for CSV
      const allLogsWithLocalTime = allLogs.map((log) => ({
        ...log,
        timestamp: log.timestamp
          ? dayjs
              .utc(log.timestamp)
              .tz(dayjs.tz.guess())
              .format("YYYY-MM-DD HH:mm:ss:SSS")
          : "",
      }));

      setDownloadProgress("Preparing CSV file...");
      downloadCSV(allLogsWithLocalTime, "all-logs.csv");
      setDownloadProgress("✅ Download complete!");
    } catch (err) {
      console.error("Error downloading all logs:", err);
      setError("Failed to download all logs.");
    } finally {
      setLoading(false);
      setTimeout(() => setDownloadProgress(null), 3000);
    }
  };

  // Get unique manufacturers from loaded devices
  const availableManufacturers = useMemo(() => {
    const manufacturers = [
      ...new Set(devices.map((device) => device.manufacturer)),
    ].filter(Boolean);
    return manufacturers.sort();
  }, [devices]);

  // Get unique manufacturers from current logs
  const logsManufacturers = useMemo(() => {
    const manufacturers = [
      ...new Set(logs.map((log) => log.manufacturer)),
    ].filter(Boolean);
    return manufacturers;
  }, [logs]);

  // Define manufacturer-specific column sets
  const manufacturerColumns = {
    // Common columns for all manufacturers
    common: [
      { field: "pk_id", headerName: "ID", width: 70 },
      { field: "uk_device_id", headerName: "Device ID", width: 200 },
      { field: "device_name", headerName: "Device Name", width: 180 },
      { field: "manufacturer", headerName: "Manufacturer", width: 120 },
      { field: "timestamp", headerName: "Timestamp", width: 180 },
      { field: "battery_level", headerName: "Battery (%)", width: 100 },
      { field: "is_charging", headerName: "Is Charging", width: 100 },
      { field: "operating_state", headerName: "Operating State", width: 130 },
    ],

    // Samsung specific columns
    "samsung electronics": [
      { field: "movement_state", headerName: "Movement State", width: 120 },
      { field: "is_charging", headerName: "Is Charging", width: 100 },
      { field: "cleaning_mode", headerName: "Mode", width: 120 },
      { field: "cleaning_area", headerName: "Area", width: 120 },
      { field: "cleaning_extent", headerName: "Extent", width: 100 },
      { field: "remaining_time", headerName: "Remaining Time", width: 130 },
      { field: "dust_bag_status", headerName: "Dust Bag", width: 120 },
      {
        field: "station_filter_status",
        headerName: "Station Filter",
        width: 130,
      },
      { field: "motor_filter_status", headerName: "Motor Filter", width: 120 },
      { field: "current_position", headerName: "Position", width: 150 },
    ],

    // Bosch specific columns
    bosch: [
      { field: "cleaning_mode", headerName: "Cleaning Mode", width: 120 },
      { field: "cleaning_step", headerName: "Cleaning Step", width: 150 },
      { field: "response_codes", headerName: "Response Codes", width: 120 },
      { field: "homing_reason", headerName: "Homing Reason", width: 150 },
      { field: "cleaning_area", headerName: "Area", width: 120 },
      { field: "cleaning_extent", headerName: "Extent", width: 100 },
      { field: "remaining_time", headerName: "Remaining Time", width: 130 },
      { field: "dust_bag_status", headerName: "Dust Bag", width: 120 },
      { field: "current_position", headerName: "Position", width: 150 },
    ],

    // Lionsbot specific columns
    lionsbot: [
      { field: "response_codes", headerName: "Response Codes", width: 120 },
      { field: "state_code", headerName: "State Code", width: 100 },
      { field: "battery_voltage", headerName: "Battery Voltage", width: 120 },
      { field: "clean_water_level", headerName: "Clean Water", width: 110 },
      { field: "dirty_water_level", headerName: "Dirty Water", width: 110 },
      { field: "bumper_detached", headerName: "Bumper Detached", width: 120 },
      { field: "handlebar_status", headerName: "Handlebar Status", width: 130 },
      { field: "localized", headerName: "Localized", width: 100 },
      {
        field: "in_restricted_zone",
        headerName: "Restricted Zone",
        width: 130,
      },
    ],

    // All available columns (for show all option)
    all: [
      { field: "pk_id", headerName: "ID", width: 70 },
      { field: "uk_device_id", headerName: "Device ID", width: 200 },
      { field: "device_name", headerName: "Device Name", width: 180 },
      { field: "manufacturer", headerName: "Manufacturer", width: 120 },
      { field: "timestamp", headerName: "Timestamp", width: 180 },
      { field: "battery_level", headerName: "Battery (%)", width: 100 },
      { field: "battery_voltage", headerName: "Battery Voltage", width: 120 },
      { field: "is_charging", headerName: "Is Charging", width: 100 },
      { field: "operating_state", headerName: "Operating State", width: 130 },
      { field: "movement_state", headerName: "Movement State", width: 120 },
      { field: "cleaning_mode", headerName: "Mode", width: 120 },
      { field: "homing_reason", headerName: "Homing Reason", width: 150 },
      { field: "cleaning_area", headerName: "Area", width: 120 },
      { field: "cleaning_extent", headerName: "Extent", width: 100 },
      { field: "remaining_time", headerName: "Remaining Time", width: 130 },
      { field: "cleaning_step", headerName: "Cleaning Step", width: 150 },
      { field: "cleaning_type", headerName: "Type", width: 140 },
      { field: "near_object", headerName: "Near Object", width: 120 },
      { field: "blocking_status", headerName: "Blocking Status", width: 130 },
      { field: "current_position", headerName: "Position", width: 150 },
      {
        field: "station_filter_status",
        headerName: "Station Filter",
        width: 130,
      },
      { field: "motor_filter_status", headerName: "Motor Filter", width: 120 },
      { field: "dust_bag_status", headerName: "Dust Bag", width: 120 },
      { field: "microphone_state", headerName: "Mic State", width: 120 },
      { field: "bumper_detached", headerName: "Bumper Detached", width: 120 },
      { field: "handlebar_status", headerName: "Handlebar Status", width: 130 },
      {
        field: "clean_water_level",
        headerName: "Clean Water Level",
        width: 140,
      },
      {
        field: "dirty_water_level",
        headerName: "Dirty Water Level",
        width: 140,
      },
      { field: "localized", headerName: "Localized", width: 100 },
      {
        field: "in_restricted_zone",
        headerName: "In Restricted Zone",
        width: 150,
      },
      { field: "response_codes", headerName: "Response Codes", width: 500 },
    ],
  };

  // Determine which columns to show based on selection
  const activeColumns = useMemo(() => {
    if (showAllColumns) {
      return manufacturerColumns.all;
    }

    if (selectedManufacturer === "all") {
      // Show common columns plus any specific columns for manufacturers present in current logs
      let columns = [...manufacturerColumns.common];

      logsManufacturers.forEach((manufacturer) => {
        if (manufacturerColumns[manufacturer]) {
          // Add manufacturer-specific columns, avoiding duplicates
          const existingFields = new Set(columns.map((col) => col.field));
          manufacturerColumns[manufacturer].forEach((col) => {
            if (!existingFields.has(col.field)) {
              columns.push(col);
              existingFields.add(col.field);
            }
          });
        }
      });

      return columns;
    }

    // Show common + specific manufacturer columns
    const specificColumns = manufacturerColumns[selectedManufacturer] || [];
    return [...manufacturerColumns.common, ...specificColumns];
  }, [selectedManufacturer, showAllColumns, logsManufacturers]);

  return (
    <>
      <TopNavBar
        title="Robot Cleaner Dashboard"
        username={username}
        heading={"Robot Cleaner Logs"}
      />
      <Container sx={{ mt: 4 }}>
        <Typography variant="h4" gutterBottom>
          Search Logs
        </Typography>

        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
          <Autocomplete
            multiple
            options={devices}
            getOptionLabel={(option) =>
              option ? `${option.name} (${option.uk_device_id})` : ""
            }
            renderInput={(params) => (
              <TextField {...params} label="Devices" variant="outlined" />
            )}
            value={devices.filter((d) =>
              selectedDevices.includes(d.uk_device_id),
            )}
            onChange={(e, newValue) =>
              setSelectedDevices(newValue.map((d) => d.uk_device_id))
            }
            sx={{ minWidth: 400 }}
          />

          <Box sx={{ display: "flex", gap: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                label="Start"
                value={start}
                onChange={(newValue) => setStart(newValue)}
                ampm={false}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                label="End"
                value={end}
                onChange={(newValue) => setEnd(newValue)}
                ampm={false}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            mb: 2,
            alignItems: "center",
          }}
        >
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Filter by Manufacturer</InputLabel>
            <Select
              value={selectedManufacturer}
              onChange={(e) => setSelectedManufacturer(e.target.value)}
              label="Filter by Manufacturer"
            >
              <MenuItem value="all">All Fields</MenuItem>
              {availableManufacturers.map((manufacturer) => (
                <MenuItem key={manufacturer} value={manufacturer.toLowerCase()}>
                  {manufacturer.charAt(0).toUpperCase() + manufacturer.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={showAllColumns}
                onChange={(e) => setShowAllColumns(e.target.checked)}
              />
            }
            label="Show All Columns"
          />

          <Button variant="contained" onClick={handleSearch}>
            Search
          </Button>
          <Button
            variant="outlined"
            onClick={handleDownloadAll}
            disabled={logs.length === 0}
          >
            Download CSV
          </Button>
          {downloadProgress && (
            <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
              {downloadProgress}
            </Typography>
          )}
        </Box>

        {error && <Typography color="error">{error}</Typography>}

        {logs.length > 0 ? (
          <>
            <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
              Showing {activeColumns.length} columns
              {!showAllColumns && selectedManufacturer !== "all" && (
                <>
                  {" "}
                  for{" "}
                  {selectedManufacturer.charAt(0).toUpperCase() +
                    selectedManufacturer.slice(1)}
                </>
              )}
              {!showAllColumns &&
                selectedManufacturer === "all" &&
                logsManufacturers.length > 0 && (
                  <>
                    {" "}
                    (combined view for:{" "}
                    {logsManufacturers
                      .map((m) => m.charAt(0).toUpperCase() + m.slice(1))
                      .join(", ")}
                    )
                  </>
                )}
            </Typography>
            <div style={{ height: 600, width: "100%" }}>
              <DataGrid
                rows={logs}
                columns={activeColumns}
                getRowId={(row) => row.pk_id}
                paginationMode="server"
                rowCount={totalCount}
                loading={loading}
                paginationModel={paginationModel}
                onPaginationModelChange={(model) => setPaginationModel(model)}
                pageSizeOptions={[10, 25, 50, 100]}
              />
            </div>
          </>
        ) : (
          <>
            <Typography>No logs to display.</Typography>
          </>
        )}
      </Container>
    </>
  );
}
